import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { MatSnackBar } from "@angular/material";
import {
  AdminApi,
  DonationCategory,
  DonationCategoryApi,
  Emoji,
  EmojiApi,
  GameCategory,
  GameCategoryApi,
  Language,
  LanguageApi,
  LoopBackConfig,
  Organization,
  OrganizationApi,
  Resource,
  ResourceApi,
  Translation,
} from "@lbsdk/*";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { environment } from "@env/environment";
import * as _ from "lodash";

import ISO6391 from "iso-639-1";
import languages from "google-tts-languages";
import { allResources } from "./allResources/all-resources";
import { HelperService } from "@app/main/core/services/helper.service";
import { E } from "@angular/core/src/render3";
import { map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class GeneralSettingsService {
  languages: Language[] = [];

  googleSupportedLanguages: any[] = [];

  resourceNamesForTranslation = [
    "messages",
    "profile",
    "settings",
    "marketplace",
    "gameButtons",
    "words",
    "registrationForm",
    "gameOverScreen",
    "splashScreens",
    "splashScreenStyles",
    "categories",
  ];

  allResources: any = allResources;

  resources: Resource[] = [];
  defaultEmojis: Emoji[] = [];
  onResourcesChanged: BehaviorSubject<any> = new BehaviorSubject({});

  categories: GameCategory[] = [];
  onCategoriesChanged: BehaviorSubject<any> = new BehaviorSubject({});

  donationCategories: DonationCategory[] = [];
  onDonationCategoriesChanged: BehaviorSubject<any> = new BehaviorSubject({});

  organizations: Organization[] = [];
  onOrganizationsChanged: BehaviorSubject<any> = new BehaviorSubject({});

  onDefaultChanged: BehaviorSubject<any> = new BehaviorSubject({});
  emojiUploadPath: string;

  onLanguagesChanged: BehaviorSubject<any> = new BehaviorSubject({});

  resourcesWithTranslations: Resource[] = [];
  onResourcesWithTranslationsChanged: BehaviorSubject<any> =
    new BehaviorSubject({});

  categoriesWithTranslations: GameCategory[] = [];
  onCategoriesWithTranslationsChanged: BehaviorSubject<any> =
    new BehaviorSubject({});

  selectedLanguage: any = null;

  defaultLanguage: any = null;

  noOfCategories: number = 0;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private resourceApi: ResourceApi,
    private languageApi: LanguageApi,
    private categoryApi: GameCategoryApi,
    private fuseSplashScreen: FuseSplashScreenService,
    private emojiApi: EmojiApi,
    private adminApi: AdminApi,
    private donationCategoryApi: DonationCategoryApi,
    private organizationApi: OrganizationApi,
    private _helperService: HelperService
  ) {
    this.googleSupportedLanguages = languages;
    this.emojiUploadPath = environment.mainContainer + "/" + environment.emojis;

    this.allResources.language[2].content.data.languages = this.languages;
  }

  /**
   * Resolve
   * @param {ActivatedRouteSnapshot} route
   * @param {RouterStateSnapshot} state
   * @returns {Observable<any> | Promise<any> | any}
   */
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> | Promise<any> | any {
    return new Promise<void>((resolve, reject) => {
      Promise.all([
        this.getResources(),
        this.getCategories(),
        this.getDonationCategories(),
        this.getEmojis(),
        this.getLanguages(),
        this.getGoogleSupportedLanguages(),
        this.getDefaultLanguage(),
        this.getCategoriesCount(),
      ]).then(() => {
        if (this.languages.length !== 0) {
          this.getResourcesWithTranslations(this.languages[0].id).then(
            (resources) => {
              this.getCategoriesWithTranslations(this.languages[0].id).then(
                (categories) => {
                  this.resourcesWithTranslations = resources;
                  this.categoriesWithTranslations = categories;
                  resolve();
                }
              );
            }
          );
        }
        resolve();
      }, reject);
    });
  }

  getCategoriesCount(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.categoryApi.count().subscribe(
        (response) => {
          this.noOfCategories = response.count;
          resolve(this.noOfCategories);
        },
        (error) => reject(error)
      );
    });
  }

  getDefaultLanguage(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.languageApi.find({ where: { name: "English" } }).subscribe(
        (response) => {
          this.defaultLanguage = response[0];
          resolve(response);
        },
        (error) => reject(error)
      );
    });
  }

  checkRequiredFields(): boolean {
    return this.getMissingRequiredFields().length > 0;
  }

  getMissingRequiredFields(): { section: string; fields: string[] }[] {
    const missing: { section: string; fields: string[] }[] = [];
    const checkSections = ["generalSetting", "rewardPoints", "messages"];

    checkSections.forEach((sectionKey) => {
      const items = this.allResources[sectionKey];
      if (Array.isArray(items)) {
        const emptyItems = items.filter(
          (el) =>
            el.isRequired &&
            (!el.content ||
              !el.content.data ||
              el.content.data.value === "" ||
              el.content.data.value === null ||
              el.content.data.value === undefined)
        );
        if (emptyItems.length > 0) {
          const formattedSection = sectionKey
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
          const fields = emptyItems.map(
            (item) =>
              item.title ||
              (item.content && item.content.data && item.content.data.key) ||
              "Field"
          );
          missing.push({ section: formattedSection, fields });
        }
      }
    });

    return missing;
  }

  getDonationCategories(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.donationCategoryApi.find().subscribe(
        (donationCategories: any[]) => {
          this.donationCategories = donationCategories;
          this.onDonationCategoriesChanged.next(this.donationCategories);
          resolve(this.donationCategories);
        },
        (error) => reject(error)
      );
    });
  }

  getResourcesWithTranslations(languageId): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.resourceApi
        .getResourceWithTranslations(languageId)
        .subscribe((resources) => {
          this.resourcesWithTranslations = resources;
          this.onResourcesWithTranslationsChanged.next(
            this.resourcesWithTranslations
          );
          resolve(resources);
        }, reject);
    });
  }

  getResources(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resourceApi.find().subscribe((response: Resource[]) => {
        this.resources = response;
        this.onResourcesChanged.next(this.resources);
        resolve(response);
      }, reject);
    });
  }

  getLanguages(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.languageApi.find().subscribe((languages: Language[]) => {
        this.languages = languages;
        this.onLanguagesChanged.next(this.languages);
        resolve(languages);
      }, reject);
    });
  }

  createOrPatchLanguage(language: Language): Promise<any> {
    language.iconUrl = language.iconUrl.replace(environment.s3BucketUrl, "");
    let lang = this.languages.filter((item) =>
      _.isEqual(item.name, language.name)
    );
    if (lang.length !== 0) {
      return new Promise<any>((resolve, reject) => {
        this.languageApi
          .patchAttributes(lang[0].id, language)
          .subscribe((language) => {
            resolve(language);
          }, reject);
      });
    } else {
      return new Promise<any>((resolve, reject) => {
        this.languageApi.create(language).subscribe((language) => {
          resolve(language);
        }, reject);
      });
    }
  }

  getEmojis(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.emojiApi
        .find({
          where: { and: [{ emojiType: "default" }, { isActive: true }] },
        })
        .subscribe((response: Emoji[]) => {
          this.defaultEmojis = response;
          this.onDefaultChanged.next(response);
          resolve(response);
        }, reject);
    });
  }

  getCategoriesWithTranslations(languageId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.categoryApi
        .find({
          include: {
            relation: "translations",
            scope: { where: { languageId: languageId } },
          },
        })
        .subscribe((categories: any) => {
          this.categoriesWithTranslations = categories;
          this.onCategoriesWithTranslationsChanged.next(categories);
          resolve(categories);
        }, reject);
    });
  }

  getCategories(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.categoryApi.find().subscribe((response: any) => {
        this.categories = response;
        this.onCategoriesChanged.next(this.categories);
        resolve(response);
      }, reject);
    });
  }

  uploadResource(file: string, uploadPath: string, adminId: string) {
    const formData = new FormData();
    formData.append("file", file);

    const headers = new HttpHeaders();
    headers.append("Content-type", "multipart/form-data");
    headers.append("Accept", "application/json");

    const params = {
      access_token: adminId,
    };

    const url: string =
      LoopBackConfig.getPath() +
      "/" +
      LoopBackConfig.getApiVersion() +
      "/containers/" +
      encodeURIComponent(uploadPath) +
      "/upload";
    return this.http.post(url, formData, { headers: headers, params: params });
  }

  saveResources(data): Promise<any> {
    return new Promise((resolve, reject) => {
      this.fuseSplashScreen.show();
      this.resourceApi.upsertResourcesAndTranslations(data).subscribe(
        (res) => {
          this.fuseSplashScreen.hide();
          resolve(res);
        },
        (error) => reject(error)
      );
    });
  }

  saveCategory(category): Promise<any> {
    category.image = category.image.replace(
      "https://emoji-storage.s3.us-east-2.amazonaws.com/",
      ""
    );
    if (category.id) {
      return new Promise((resolve, reject) => {
        this.categoryApi
          .patchAttributes(category.id, category)
          .subscribe((response: any) => {
            this.categories = response;
            this.onCategoriesChanged.next(this.categories);
            resolve(response);
          }, reject);
      });
    } else {
      return new Promise((resolve, reject) => {
        this.categoryApi.create(category).subscribe((response: any) => {
          this.categories = response;
          this.onCategoriesChanged.next(this.categories);
          resolve(response);
        }, reject);
      });
    }
  }

  getTranslations(form: any, language, type): any[] {
    let translations = form.profileResourceTranslations;
    translations.push(...form.settingResourceTranslations);
    translations.push(...form.marketplaceResourceTranslations);
    translations.push(...form.buttonResourceTranslations);
    translations.push(...form.splashscreenResourceTranslations);
    translations.push(...form.categoryTranslations);

    translations =
      type === "categories"
        ? translations.filter((el) => el.translationableType === "GameCategory")
        : translations.filter((el) => el.translationableType === "Resource");

    translations.forEach(
      (el) =>
        (el.languageId =
          el.languageId !== "" ? el.languageId : language ? language.id : "")
    );
    if (type === "resources") {
      if (language.id !== "") {
        let resourcesTranslations = this.resourcesWithTranslations.filter(
          (el) => el.translations.length !== 0
        );
        if (resourcesTranslations.length !== 0) {
          return translations
            .filter(
              (o1) =>
                resourcesTranslations.filter(
                  (o2) => o2.translations[0].name === o1.name
                ).length === 0
            )
            .filter((el) => el.name !== "");
        }
      } else {
        return translations.filter((el) => el.name !== "");
      }
    } else if (type === "categories") {
      if (language.id !== "") {
        let categoriesTranslations = this.categoriesWithTranslations.filter(
          (el) => el.translations.length !== 0
        );
        if (categoriesTranslations.length !== 0) {
          return translations
            .filter(
              (o1) =>
                categoriesTranslations.filter(
                  (o2) => o2.translations[0].name === o1.name
                ).length === 0
            )
            .filter((el) => el.name !== "");
        }
      } else return translations.filter((el) => el.name !== "");
    } else return [];
  }

  onResize(event) {
    let breakpoint: any;
    if (event) {
      if (event <= 900 || event <= 1125 || event <= 1500) {
        breakpoint = event <= 900 ? 1 : 6;
      }
      if (event <= 800) {
        breakpoint = event <= 800 ? 5 : 6;
      }
      if (event <= 600) {
        breakpoint = event <= 600 ? 4 : 6;
      }
      if (event <= 500) {
        breakpoint = event <= 500 ? 3 : 6;
      }
      if (event <= 400) {
        breakpoint = event <= 400 ? 2 : 6;
      }
    }
    if (event.target) {
      if (
        event.target.innerWidth <= 900 ||
        event.target.innerWidth <= 1125 ||
        event.target.innerWidth <= 1500
      ) {
        breakpoint = event.target.innerWidth <= 900 ? 1 : 6;
      }
      if (event.target.innerWidth <= 800 || event.target.innerWidth <= 899) {
        breakpoint = event.target.innerWidth <= 800 ? 5 : 6;
      }
      if (event.target.innerWidth <= 600) {
        breakpoint = event.target.innerWidth <= 600 ? 4 : 6;
      }
      if (event.target.innerWidth <= 500) {
        breakpoint = event.target.innerWidth <= 500 ? 3 : 6;
      }
      if (event.target.innerWidth <= 400) {
        breakpoint = event.target.innerWidth <= 400 ? 2 : 6;
      }
    }
    return breakpoint !== undefined ? breakpoint : 8;
  }

  deleteDonationCategoryById(categoryId) {
    return this.donationCategoryApi.deleteById(categoryId);
  }

  createDonationCategory(data) {
    return this.donationCategoryApi.create(data);
  }

  getOrganizations(donationCategoryId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.organizationApi
        .find({ where: { donationCategoryId: donationCategoryId } })
        .subscribe(
          (response: Organization[]) => {
            this.organizations = response;
            this.onOrganizationsChanged.next(this.organizations);
            resolve(response);
          },
          (error) => reject(error)
        );
    });
  }

  addOrganization(data) {
    return this.organizationApi.create(data);
  }

  editOrganization(organizationId, data) {
    if (data.logo === null) delete data.logo;
    else
      data.logo =
        data.logo && data.logo.results
          ? data.logo.result.files["logo"][0]["providerResponse"].name
          : data.logo;

    if (data.thankYouImg === null) delete data.thankYouImg;
    else
      data.thankYouImg =
        data.thankYouImg && data.thankYouImg.result
          ? data.thankYouImg.result.files["thankYouImg"][0]["providerResponse"]
              .name
          : data.thankYouImg;

    return this.organizationApi.patchAttributes(organizationId, data);
  }

  uploadCategoryImage(url, formData) {
    const headers = new HttpHeaders();
    headers.append("Content-type", "multipart/form-data");
    headers.append("Accept", "application/json");

    const params = {
      access_token: this.adminApi.getCurrentToken().id,
    };
    return this.http.post(url, formData, { headers: headers, params: params });
  }

  getGoogleSupportedLanguages(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          "https://translation.googleapis.com/language/translate/v2/languages?key=" +
            environment.googleApiKey
        )
        .subscribe(
          (response: any) => {
            let languages = [];
            for (let el of response.data.languages) {
              let name = ISO6391.getName(el.language);
              if (name !== "") {
                languages.push({
                  code: el.language,
                  name: name,
                });
              }
            }
            this.allResources.language[0].content.data.languages = languages;
            resolve(response);
          },
          (error) => reject(error)
        );
    });
  }

  private translatingFromGoogleChunk(chunk: { index: number; text: string }[], code: string): Observable<any> {
    if (!code) return null;
    return this.http.post(
      `https://translation.googleapis.com/language/translate/v2?key=${environment.googleApiKey}`,
      {
        q: chunk.map(item => item.text),
        target: code,
      }
    ).pipe(
      map(response => ({
        chunk,
        translations: response['data']['translations'],
      }))
    );
  }

  translatingFromGoogle(resourcesToTranslate: string[], code: string): Observable<any> {
    const chunkSize = 100;
    const chunks = [];

    for (let i = 0; i < resourcesToTranslate.length; i += chunkSize) {
      chunks.push(resourcesToTranslate.slice(i, i + chunkSize).map((text, index) => ({ index: i + index, text })));
    }

    const requests = chunks.map(chunk => this.translatingFromGoogleChunk(chunk, code));

    return forkJoin(requests).pipe(
      map((responses: any[]) => {
        const translations = new Array(resourcesToTranslate.length);
        responses.forEach(({ chunk, translations: chunkTranslations }) => {
          chunk.forEach((item, index) => {
            translations[item.index] = chunkTranslations[index].translatedText;
          });
        });
        return translations;
      })
    );
  }

  getTranslationWithLangId(langId): Promise<any> {
    let requests = [
      this.languageApi.getTranslations(langId),
      this.resourceApi.find({
        where: { languageId: langId, type: "gameOver" },
      }),
    ];
    return new Promise((resolve, reject) => {
      forkJoin(requests).subscribe(
        (response) => {
          resolve({
            translations: response[0],
            resources: response[1],
          });
        },
        (error) => reject(error)
      );
    });
  }

  deleteLanguageAndTranslations(langId) {
    return this.languageApi.deleteLanguageAndTranslations(langId);
  }

  deleteOrgranization(organizationId) {
    return this.organizationApi.deleteById(organizationId);
  }

  updatingTranslations = async (updatedResources): Promise<any[]> => {
    let translations = [];

    for await (let el of updatedResources) {
      el.resource.value =
        el.translation && el.translation.name
          ? el.translation.name
          : el.resource.value;
      try {
        let response = await this.resourceApi
          .getTranslations(el.resource.id)
          .toPromise();
        if (!response) break;
        translations = translations.concat(response);
      } catch (error) {
        console.log(error);
        break;
      }
    }

    if (translations.length !== 0) {
      let index = 0;
      for await (let el of translations) {
        let text = updatedResources.find(
          (element) => element.resource.id === el.translationableId
        ).resource;

        if (text) {
          if (
            text.key === "automaticMessage" ||
            text.key === "automaticDonationMessage" ||
            text.key === "socialLoginPrompt"
          )
            text = {
              ...text,
              value: this._helperService.getValue(
                text.key,
                text.value,
                "encrypt"
              ),
            };
          let language = this.languages.find(
            (element) => element.id === el.languageId
          );
          if (language) {
            let code = ISO6391.getCode(language.name);
            try {
              const res: any = await this.translatingFromGoogle(
                text.value,
                code
              ).toPromise();
              if (!res) continue;
              translations[index].name =
                res.data.translations[0].translatedText;
            } catch (error) {
              continue;
            }
          }
        }
        index++;
      }
    }
    return translations;
  };
}
